import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { OWN_ERROR_TOAST } from 'src/app/shared/interceptors/http-error.interceptor';
import {
  ConfigurationAudit,
  confirmationPayload,
  GlobalConfiguration,
  GlobalConfigurationValue,
} from 'src/app/models/GlobalConfiguration';
import { PaginatedResult } from '../model/paginated-result';

/**
 * Status que significam "a rota de histórico ainda não existe neste ambiente".
 *
 * A gravação da auditoria já é feita pelo backend; a LEITURA está sendo
 * construída em paralelo. Enquanto ela não sobe, o painel de histórico mostra um
 * aviso e a tela de configuração continua inteira — deixar a tela quebrar por
 * causa de uma rota ausente seria trocar a função principal pela secundária.
 *
 * 404 é a rota inexistente, 405 é o verbo recusado por uma rota parecida e 501 é
 * a rota declarada mas não implementada.
 */
const HISTORY_ABSENT_STATUSES = [404, 405, 501];

@Injectable({ providedIn: 'root' })
export class GlobalConfigurationService {

  constructor(private http: HttpClient) {}

  /** O catálogo inteiro, na ordem em que o backend o declara. */
  list(): Observable<GlobalConfiguration[]> {
    return this.http.get<GlobalConfiguration[]>(`${environment.apiUrl}/admin/configurations`);
  }

  /**
   * Uma chave por requisição — é assim que a API e a auditoria funcionam.
   *
   * `confirmed` só é passado pela tela depois do passo de confirmação de uma
   * chave de cobrança (CU-86akkmfhx); sem ele o corpo continua `{ value }`.
   */
  update(key: string, value: GlobalConfigurationValue, confirmed: boolean = false): Observable<GlobalConfiguration> {
    const body = confirmed ? { value, ...confirmationPayload() } : { value };

    // A tela dá os próprios avisos (OWN_ERROR_TOAST): no status 0 o "Tente novamente mais tarde"
    // do interceptador contradizia o "pode ter sido gravada, recarregue antes de salvar de novo".
    return this.http.put<GlobalConfiguration>(
      `${environment.apiUrl}/admin/configurations/${encodeURIComponent(key)}`,
      body,
      { context: new HttpContext().set(OWN_ERROR_TOAST, true) }
    );
  }

  history(key: string, page: number = 1, perPage: number = 10): Observable<PaginatedResult<ConfigurationAudit[]>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('per_page', String(perPage));

    return this.http.get<PaginatedResult<ConfigurationAudit[]>>(
      `${environment.apiUrl}/admin/configurations/${encodeURIComponent(key)}/history`,
      { params }
    );
  }
}

/** True quando a falha é a ausência da rota de histórico, e não um erro real. */
export function isHistoryUnavailable(error: HttpErrorResponse): boolean {
  return !!error && HISTORY_ABSENT_STATUSES.indexOf(error.status) >= 0;
}

/**
 * Status 0 (a resposta não chegou) ou 5xx: o PUT pode ter sido aplicado. Não é falha certa —
 * a tela manda recarregar e conferir o valor em vigor, em vez de dizer que nada mudou.
 */
export function isConfigurationOutcomeUnknown(error: HttpErrorResponse): boolean {
  const status = error?.status;
  return status === 0 || status >= 500;
}

/**
 * As gravações de configuração (este PUT e o das faixas de bônus) levam `OWN_ERROR_TOAST`: o
 * `HttpErrorInterceptor` só avisa o 401 (sessão), e o resto — 0, 422, 5xx — é a tela que avisa,
 * uma vez, com o texto dela.
 */
export function isToastedByInterceptor(error: HttpErrorResponse): boolean {
  return error?.status === 401;
}

/**
 * Mensagem de uma falha de gravação, na ordem em que o backend responde:
 * 422 com `errors` (regra do catálogo), 422/404 com `message`, e o resto.
 *
 * Fica fora da classe porque quem trata o erro já tem a resposta na mão e não
 * precisa do cliente HTTP injetado para lê-la.
 */
export function configurationErrorMessage(error: HttpErrorResponse, fallback: string): string {
  const body = (error && error.error) || {};
  const errors = body.errors;

  if (errors) {
    const firstField = Object.keys(errors)[0];
    const messages = firstField ? errors[firstField] : null;

    if (messages) {
      return Array.isArray(messages) ? messages[0] : String(messages);
    }
  }

  return body.message || fallback;
}
